import { Link } from "@tanstack/react-router";
import { CheckCircle, User, Calendar, Shield, Building, Award, Wrench, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionReveal from "@/components/SectionReveal";
import WaveDivider from "@/components/WaveDivider";
import CTAStrip from "@/components/CTAStrip";

const differentiators = [
  {
    title: "35 Years of Hands-On Experience",
    desc: "We've been installing floors for over three decades. That's thousands of homes and commercial spaces — each one a testament to our craftsmanship and commitment to getting every detail right.",
  },
  {
    title: "We Handle Everything",
    desc: "From sourcing the right materials to managing every step of the installation, JTB Floors takes care of everything. You won't need to coordinate with suppliers or worry about logistics — we buy all the materials and deliver a finished floor.",
  },
  {
    title: "Residential & Commercial",
    desc: "Whether it's a family home, a rental property, or a commercial space, we bring the same level of quality and attention. No job is too big or too small.",
  },
  {
    title: "100% Satisfaction Guarantee",
    desc: "Every project is backed by our satisfaction guarantee on all labor. If something doesn't look right, we'll come back and make it right — no questions asked.",
  },
  {
    title: "Water Damage Repairs",
    desc: "We'll repair water-damaged flooring even if the damage wasn't caused by our work. That's how strongly we believe in taking care of our customers.",
  },
];

const About = () => {
  return (
    <Layout>
      <SEOHead
        title="About JTB Floors | Barrington, IL"
        description="35 years of professional flooring experience serving Barrington, IL and surrounding areas. Hardwood, ceramic, laminate, tile & marble. BBB accredited."
      />

      {/* Hero */}
      <section className="py-16 lg:py-24 bg-muted">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionReveal>
            <h1 className="text-4xl md:text-5xl font-heading font-black mb-4">About JTB Floors</h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              For over 35 years, JTB Floors has been the name homeowners and businesses trust for quality flooring in the Barrington, IL area. We specialize in hardwood and offer a full range of flooring services — all backed by a commitment to craftsmanship that never cuts corners.
            </p>
          </SectionReveal>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--muted))" bottomColor="hsl(var(--background))" />

      {/* Owner Story */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <SectionReveal>
            <h2 className="text-3xl font-heading font-bold mb-8">Our Story</h2>
          </SectionReveal>
          <div className="space-y-6">
            <SectionReveal delay={0.1}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
                <p className="text-foreground leading-relaxed">
                  With <strong>over 35 years in the flooring business</strong>, JTB Floors has built a reputation for quality craftsmanship and reliable service across the Barrington, IL area and within a 35-mile radius.
                </p>
              </div>
            </SectionReveal>
            <SectionReveal delay={0.2}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <p className="text-foreground leading-relaxed">
                  We specialize in <strong>hardwood flooring</strong> and also offer ceramic, laminate, hard tile, and marble installation. Every project includes materials, labor, and a finished product you'll love.
                </p>
              </div>
            </SectionReveal>
            <SectionReveal delay={0.3}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Building className="w-5 h-5 text-primary" />
                </div>
                <p className="text-foreground leading-relaxed">
                  JTB Floors serves both <strong>residential and commercial</strong> clients, bringing the same attention to detail whether it's a family home or a business property.
                </p>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--background))" bottomColor="hsl(var(--muted))" />

      {/* What Sets Us Apart */}
      <section className="py-16 lg:py-24 bg-muted">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <SectionReveal>
            <h2 className="text-3xl font-heading font-bold mb-4">What Sets Us Apart</h2>
            <p className="text-muted-foreground mb-10 max-w-2xl">
              There are plenty of flooring contractors out there. Here's why homeowners and businesses across the Barrington area keep choosing JTB Floors.
            </p>
          </SectionReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {differentiators.map((item, i) => (
              <SectionReveal key={i} delay={i * 0.08}>
                <div className="bg-card border border-border rounded-xl p-6 h-full hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <h3 className="font-heading font-semibold">{item.title}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
          <SectionReveal delay={0.4}>
            <div className="flex items-start gap-3 mt-8">
              <Shield className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <p className="text-muted-foreground text-sm italic">BBB accredited. Liability insurance available upon request.</p>
            </div>
          </SectionReveal>
          <SectionReveal delay={0.5}>
            <div className="mt-10">
              <Button asChild className="btn-shine gap-2">
                <a href="tel:17739342004">
                  <Phone className="w-4 h-4" /> Call (773) 934-2004
                </a>
              </Button>
            </div>
          </SectionReveal>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--muted))" bottomColor="hsl(var(--background))" />

      {/* Guarantee & Credentials */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <SectionReveal>
            <h2 className="text-3xl font-heading font-bold mb-8">Our Guarantee & Credentials</h2>
          </SectionReveal>
          <SectionReveal delay={0.1}>
            <div className="bg-card border border-border rounded-xl p-8 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-7 h-7 text-primary" />
                <h3 className="text-xl font-heading font-bold">100% Satisfaction Guarantee</h3>
              </div>
              <p className="text-foreground leading-relaxed mb-4">
                We stand behind every job. If you're not happy with the work, we'll make it right — no questions asked.
              </p>
              <div className="flex items-start gap-3 p-4 bg-primary/5 rounded-lg">
                <Wrench className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-foreground text-sm">
                  <strong>Water damage?</strong> We'll still fix the flooring — even if it's not our fault. That's how much we stand behind our craft.
                </p>
              </div>
            </div>
          </SectionReveal>
          <SectionReveal delay={0.2}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Award, title: "BBB Accredited", desc: "Committed to trust and quality service." },
                { icon: Shield, title: "Liability Insurance", desc: "Available upon request when required." },
              ].map((d, i) => (
                <div key={i} className="bg-card border border-border rounded-xl p-6 text-center hover:shadow-lg transition-shadow duration-300">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <d.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold mb-1">{d.title}</h3>
                  <p className="text-muted-foreground text-sm">{d.desc}</p>
                </div>
              ))}
            </div>
          </SectionReveal>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--background))" bottomColor="hsl(var(--secondary))" />

      <CTAStrip />
    </Layout>
  );
};

export default About;
